import Sigma from "sigma";
import {Annotation, Region} from "../types/Types";
import {lightenHexColor} from "../utils/colour";
import {drawSvgLegend, LegendItem} from "./svgLegend";
import {createSvg, serializeSvg} from "./svgUtils";

const useNetworkSvg = (sigma?: Sigma | null, regions?: Region[] | null, legendAnnotations?: Annotation[] | null, showLegend = false) => {
  function getNodeScaleFactor(ratio: number) {
    const minRatio = 1;      // neutral zoom
    const maxRatio = 0.1;    // high zoom (adjust based on your use case)
    const minFactor = 0.6;
    const maxFactor = 1;

    if (ratio <= maxRatio) return maxFactor; // fully zoomed in
    if (ratio >= minRatio) return minFactor; // fully zoomed out

    // Linear interpolation
    return minFactor + (maxFactor - minFactor) * (minRatio - ratio) / (minRatio - maxRatio);
  }

  function projectWithCamera(sigma: Sigma, attrs: any, width: number, height: number) {
    const {x, y} = sigma.graphToViewport(attrs);

    return {
      x, y,
      size: sigma.scaleSize(attrs.size) * getNodeScaleFactor(sigma.getCamera().getState().ratio),
      visible: isVisible({x, y}, width, height)
    };
  }

  function isVisible(
    p: { x: number; y: number },
    width: number,
    height: number,
    margin = 0
  ) {
    return (
      p.x >= -margin &&
      p.y >= -margin &&
      p.x <= width + margin &&
      p.y <= height + margin
    );
  }

  return (): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      if (!sigma) {
        reject(new Error("Sigma instance is not available"));
        return;
      }

      const graph = sigma.getGraph();
      const {width, height} = sigma.getDimensions();
      const settings = sigma.getSettings();

      const svg = createSvg(width, height);

      // Regions
      if (regions?.length) {
        const defs = document.createElementNS(svg.namespaceURI, "defs");
        svg.appendChild(defs);

        const zero = sigma.graphToViewport({x: 0, y: 0}).x;

        regions.forEach((region, i) => {
          const {x, y} = sigma.graphToViewport({x: region.x, y: region.y});
          const r = (sigma.graphToViewport({x: region.r, y: 0}).x - zero) * 1.5;

          // Radial gradient matching canvas version
          const grad = document.createElementNS(svg.namespaceURI, "radialGradient");
          const gradId = `region-grad-${i}`;
          grad.setAttribute("id", gradId);
          grad.setAttribute("gradientUnits", "userSpaceOnUse");
          grad.setAttribute("cx", String(x));
          grad.setAttribute("cy", String(y));
          grad.setAttribute("r", String(r * 0.9));
          grad.setAttribute("fx", String(x));
          grad.setAttribute("fy", String(y));
          grad.setAttribute("fr", String(r * 0.06));

          const stop0 = document.createElementNS(svg.namespaceURI, "stop");
          stop0.setAttribute("offset", "0%");
          stop0.setAttribute("stop-color", region.colour);
          stop0.setAttribute("stop-opacity", "0.65");
          grad.appendChild(stop0);

          const stop1 = document.createElementNS(svg.namespaceURI, "stop");
          stop1.setAttribute("offset", "100%");
          stop1.setAttribute("stop-color", region.colour);
          stop1.setAttribute("stop-opacity", "0");
          grad.appendChild(stop1);

          defs.appendChild(grad);

          const circle = document.createElementNS(svg.namespaceURI, "circle");
          circle.setAttribute("cx", String(x));
          circle.setAttribute("cy", String(y));
          circle.setAttribute("r", String(r * 1.2));
          circle.setAttribute("fill", `url(#${gradId})`);
          svg.appendChild(circle);

          // Label (multi-line via tspan)
          const {x: lx, y: ly} = sigma.graphToViewport({x: region.label_x, y: region.label_y});
          const zoom = sigma.getCamera().ratio ** 0.7;
          const fontSize = 16 / zoom;
          const lighter = lightenHexColor(region.colour, 0.1);
          const lines = region.label.split("\n");

          const textEl = document.createElementNS(svg.namespaceURI, "text");
          textEl.setAttribute("x", String(lx));
          textEl.setAttribute("y", String(ly));
          textEl.setAttribute("font-size", String(fontSize));
          textEl.setAttribute("font-family", "Arial, sans-serif");
          textEl.setAttribute("font-weight", "bold");
          textEl.setAttribute("dominant-baseline", "middle");
          textEl.setAttribute("fill", region.colour);
          textEl.setAttribute("stroke", lighter);
          textEl.setAttribute("stroke-width", String(0.4 / zoom));
          textEl.setAttribute("paint-order", "stroke fill");

          lines.forEach((line, li) => {
            const tspan = document.createElementNS(svg.namespaceURI, "tspan");
            tspan.setAttribute("x", String(lx));
            tspan.setAttribute("dy", li === 0 ? "0" : String(fontSize));
            tspan.textContent = line;
            textEl.appendChild(tspan);
          });

          svg.appendChild(textEl);
        });
      }

      // Cache projected nodes
      const projected = new Map<string, { x: number, y: number, size: number, visible: boolean }>();
      graph.forEachNode((key, attrs) => {
        projected.set(key, projectWithCamera(sigma, attrs, width, height));
      });

      // Edges
      graph.forEachEdge((_edge, attrs, s, t) => {
        const ps = projected.get(s);
        const pt = projected.get(t);

        if (!ps || !pt) return;  // Should not happen
        if (!ps.visible && !pt.visible) return;  // One node must be visible to draw the edge

        const line = document.createElementNS(svg.namespaceURI, "line");
        line.setAttribute("x1", ps.x.toString());
        line.setAttribute("y1", ps.y.toString());
        line.setAttribute("x2", pt.x.toString());
        line.setAttribute("y2", pt.y.toString());
        line.setAttribute("stroke", attrs.color || "#999");
        line.setAttribute("stroke-width", String(sigma.scaleSize((attrs.size || 1))));
        line.setAttribute("stroke-linecap", "round");

        svg.appendChild(line);
      });

      // Draw nodes
      graph.forEachNode((node, attrs) => {
        const pn = projected.get(node);

        if (!pn?.visible) return;

        const circle = document.createElementNS(svg.namespaceURI, "circle");
        circle.setAttribute("cx", pn.x.toString());
        circle.setAttribute("cy", pn.y.toString());
        circle.setAttribute("r", (pn.size).toString());
        circle.setAttribute("fill", attrs.color || "#fff");
        circle.setAttribute("stroke", attrs.color || "#fff");
        circle.setAttribute("stroke-width", "1");

        svg.appendChild(circle);
      });

      // Draw labels
      const occupiedBoxes: { x: number; y: number; right: number; bottom: number }[] = [];

      graph.forEachNode((node, attrs) => {
        const pn = projected.get(node);

        if (!pn?.visible) return;

        // Decide if label is visible
        const scaledSize = sigma.scaleSize(attrs.size);
        const paddingX = 4;
        const paddingY = 2;
        const radius = 3;

        if (attrs.label &&
          (attrs.forceLabel ||
            scaledSize >= settings.labelRenderedSizeThreshold)        // respects Sigma's threshold
        ) {
          const text = document.createElementNS(svg.namespaceURI, "text") as SVGGraphicsElement;
          text.setAttribute("x", (pn.x + scaledSize + paddingX + 2).toString());
          text.setAttribute("y", pn.y.toString());
          text.setAttribute("font-size", "14");
          text.setAttribute("font-family", "Arial, sans-serif");
          text.setAttribute("text-anchor", "start");
          text.setAttribute("dominant-baseline", "central");
          text.setAttribute("fill", "#fff");
          text.textContent = attrs.label;
          svg.appendChild(text);

          // Measure text
          const bbox = text.getBBox();
          const box = {
            x: bbox.x - paddingX,
            y: bbox.y - paddingY,
            right: bbox.x + bbox.width + paddingX,
            bottom: bbox.y + bbox.height + paddingY,
          };

          // Skip label if it overlaps an already-rendered one
          const overlaps = occupiedBoxes.some(
            (b) => !(box.right < b.x || box.x > b.right || box.bottom < b.y || box.y > b.bottom)
          );
          if (overlaps) {
            svg.removeChild(text);
            return;
          }
          occupiedBoxes.push(box);

          // Background rect
          const rect = document.createElementNS(svg.namespaceURI, "rect");
          rect.setAttribute("x", String(box.x));
          rect.setAttribute("y", String(box.y));
          rect.setAttribute("width", String(bbox.width + paddingX * 2));
          rect.setAttribute("height", String(bbox.height + paddingY * 2));
          rect.setAttribute("rx", String(radius));
          rect.setAttribute("ry", String(radius));
          rect.setAttribute("fill", "#000");
          rect.setAttribute("stroke", "#fff");
          rect.setAttribute("stroke-width", "1");

          // Insert rect *behind* text
          svg.insertBefore(rect, text);
        }
      });

      // Legend overlay (bottom-left)
      if (showLegend) {
        const baseItems: LegendItem[] = [
          {type: "circle", colour: "#d9d9d9", label: "Gene"},
          {type: "line",   colour: "#d9d9d9", label: "Genetic interaction profile similarity (PCC)"},
        ];
        const annItems = (legendAnnotations ?? []).map(a => ({type: "circle" as const, colour: a.colour, label: a.name}));
        drawSvgLegend(svg as SVGSVGElement, height, "Legend", [baseItems, annItems]);
      }

      resolve(serializeSvg(svg));
    });
  }
};

export default useNetworkSvg;